import { TokenService } from '@/services/auth/TokenService';
import { reportNetworkFailure, reportNetworkSuccess } from './serverReachability';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787/api';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestConfig extends RequestInit {
  headers?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown
  ) {
    super(`API Error: ${status} ${statusText}`);
    this.name = 'ApiError';
  }
}

/** Build a query string from params, skipping undefined and 'all' values. */
export function buildQuery(params: Record<string, string | undefined>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v && v !== 'all') q.set(k, v);
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

function createRequest(method: HttpMethod, body?: unknown): RequestConfig {
  const config: RequestConfig = {
    method,
    headers: { 'Content-Type': 'application/json' },
  };

  const token = TokenService.getToken();
  if (token) {
    config.headers!['Authorization'] = `Bearer ${token}`;
  }

  if (body && method !== 'GET') {
    config.body = JSON.stringify(body);
  }

  return config;
}

/**
 * Códigos que indican que la INFRAESTRUCTURA no pudo atender la petición
 * (gateway/edge caído: 502-504 y los 52x de Cloudflare), no un error de la
 * aplicación. Cuentan como fallo de alcanzabilidad, igual que un rechazo
 * del propio fetch.
 */
function isInfraErrorStatus(status: number): boolean {
  return status === 502 || status === 503 || status === 504 || (status >= 520 && status <= 527);
}

/**
 * Único punto de red del cliente: todas las peticiones pasan por aquí para
 * alimentar la detección de servidor inalcanzable (serverReachability). El
 * error original se relanza intacto — los consumidores no ven diferencia.
 */
async function doFetch<T>(url: string, config: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(url, config);
  } catch (error) {
    // fetch solo rechaza por fallos de red: proveedor caído, DNS, CORS…
    reportNetworkFailure();
    throw error;
  }
  if (isInfraErrorStatus(response.status)) reportNetworkFailure();
  else reportNetworkSuccess();
  return handleResponse<T>(response);
}

async function handleResponse<T>(response: Response): Promise<T> {
  let data: unknown;
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, data);
  }

  return data as T;
}

export class ApiClient {
  static async get<T>(endpoint: string): Promise<T> {
    return doFetch<T>(`${API_BASE_URL}${endpoint}`, createRequest('GET'));
  }

  static async post<T>(endpoint: string, body?: unknown): Promise<T> {
    return doFetch<T>(`${API_BASE_URL}${endpoint}`, createRequest('POST', body));
  }

  static async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return doFetch<T>(`${API_BASE_URL}${endpoint}`, createRequest('PUT', body));
  }

  static async patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return doFetch<T>(`${API_BASE_URL}${endpoint}`, createRequest('PATCH', body));
  }

  static async delete<T>(endpoint: string): Promise<T> {
    return doFetch<T>(`${API_BASE_URL}${endpoint}`, createRequest('DELETE'));
  }

  /** POST with FormData — does NOT set Content-Type (browser sets it with the correct boundary). */
  static async postFormData<T>(endpoint: string, formData: FormData): Promise<T> {
    const headers: Record<string, string> = {};
    const token = TokenService.getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return doFetch<T>(`${API_BASE_URL}${endpoint}`, { method: 'POST', headers, body: formData });
  }
}

export default ApiClient;
